/**
 * useAnimation — Framer Motion animation hook for Cogo nodes.
 *
 * Converts Cogo AnimationConfig → Framer Motion variants/props.
 * Supports: spring physics, stagger, inView scroll trigger, enter/exit.
 */
import { useRef, useMemo } from 'react';
import { useInView } from 'motion/react';
import type { Transition, TargetAndTransition } from 'motion/react';
import type { AnimationConfig, TweenProps } from '../core/types';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export type { AnimationConfig, TweenProps };

export interface CogoNode {
    id?: string;
    options?: { animation?: AnimationConfig; [key: string]: unknown };
    animation?: AnimationConfig;
    _staggerDelay?: number;
}

export interface UseAnimationResult {
    ref: React.RefObject<HTMLDivElement | null>;
    initial: TargetAndTransition | false;
    animate: TargetAndTransition | false;
    exit: TargetAndTransition | undefined;
    transition: Transition | undefined;
    isVisible: boolean;
}

/* ------------------------------------------------------------------ */
/*  Curve / Transition mapping                                         */
/* ------------------------------------------------------------------ */

const TWEEN_CURVE_MAP: Record<string, string> = {
    'linear': 'linear',
    'ease': 'easeInOut',
    'ease-in': 'easeIn',
    'ease-out': 'easeOut',
    'ease-in-out': 'easeInOut',
};

const CONTROL_KEYS = new Set([
    'duration', 'curve', 'delay', 'easing',
    'bounce', 'stiffness', 'damping', 'mass',
]);

/** Build a Framer Motion transition from TweenProps */
function buildTransition(props: TweenProps, staggerDelay: number): Transition {
    const isSpring = props.curve === 'spring' ||
        props.bounce != null ||
        props.stiffness != null ||
        props.damping != null;

    const baseDelay = (props.delay ?? 0) / 1000 + staggerDelay / 1000;

    if (isSpring) {
        return {
            type: 'spring',
            bounce: props.bounce ?? 0.25,
            stiffness: props.stiffness,
            damping: props.damping,
            mass: props.mass,
            delay: baseDelay > 0 ? baseDelay : undefined,
        };
    }

    const duration = (props.duration ?? 300) / 1000;
    const ease = TWEEN_CURVE_MAP[props.curve ?? props.easing ?? 'ease-out'] ?? 'easeOut';

    return {
        type: 'tween',
        duration,
        ease: ease as Transition["ease"],
        delay: baseDelay > 0 ? baseDelay : undefined,
    };
}

/* ------------------------------------------------------------------ */
/*  Variant extraction                                                 */
/* ------------------------------------------------------------------ */

/** Extract "from" state (initial) from tween pairs */
function extractFromState(props: TweenProps): TargetAndTransition {
    const state: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(props)) {
        if (CONTROL_KEYS.has(key)) continue;
        if (!Array.isArray(value) || value.length < 2) continue;
        const [from] = value as [number, number];
        const cssKey = tweenKeyToMotionKey(key);
        if (cssKey) state[cssKey] = from;
    }
    return state as TargetAndTransition;
}

/** Extract "to" state (animate) from tween pairs */
function extractToState(props: TweenProps): TargetAndTransition {
    const state: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(props)) {
        if (CONTROL_KEYS.has(key)) continue;
        if (!Array.isArray(value) || value.length < 2) continue;
        const [, to] = value as [number, number];
        const cssKey = tweenKeyToMotionKey(key);
        if (cssKey) state[cssKey] = to;
    }
    return state as TargetAndTransition;
}

/** Map Cogo tween property names → Framer Motion property names */
function tweenKeyToMotionKey(key: string): string | null {
    switch (key) {
        case 'opacity': return 'opacity';
        case 'translateX': return 'x';
        case 'translateY': return 'y';
        case 'translateZ': return 'z';
        case 'scale': return 'scale';
        case 'scaleX': return 'scaleX';
        case 'scaleY': return 'scaleY';
        case 'rotate': return 'rotate';
        case 'rotateX': return 'rotateX';
        case 'rotateY': return 'rotateY';
        case 'rotateZ': return 'rotateZ';
        case 'skewX': return 'skewX';
        case 'skewY': return 'skewY';
        case 'blur': return 'filter';
        default: return null;
    }
}

/* ------------------------------------------------------------------ */
/*  useAnimation hook                                                  */
/* ------------------------------------------------------------------ */

/**
 * Framer Motion animation hook for Cogo nodes.
 *
 * @param node       The Cogo node being rendered.
 * @param childIndex Index among siblings (for stagger).
 */
export function useAnimation(
    node: CogoNode | null | undefined,
    childIndex = 0,
): UseAnimationResult {
    const ref = useRef<HTMLDivElement | null>(null);
    const animation: AnimationConfig | undefined = node?.options?.animation || node?.animation;

    const hasScrollTrigger = animation?.scroll?.trigger === 'inView';
    const scrollThreshold = animation?.scroll?.threshold ?? 0.1;
    const scrollOnce = animation?.scroll?.once !== false;

    const isInView = useInView(ref, {
        amount: scrollThreshold,
        once: scrollOnce,
    });

    // For scroll-triggered animations, visibility depends on IntersectionObserver.
    // For non-scroll animations, always visible (play immediately).
    const isVisible = hasScrollTrigger ? isInView : true;

    // Stabilize memo key: propagateStagger creates new animation objects on every
    // render (new reference). Using the object directly as a useMemo dep causes
    // recomputation → Framer Motion receives new initial/animate props → restarts
    // animation. Serializing produces a stable string key for value-equality.
    const animKey = animation ? JSON.stringify(animation) : '';

    const result = useMemo((): Omit<UseAnimationResult, 'ref' | 'isVisible'> => {
        if (!animation?.enter) {
            return { initial: false, animate: false, exit: undefined, transition: undefined };
        }

        const enter = animation.enter;
        const stagger = animation.stagger ?? 0;
        const staggerDelay = (node?._staggerDelay ?? 0) + (childIndex * stagger);

        const initial = extractFromState(enter);
        const animate = extractToState(enter);
        const transition = buildTransition(enter, staggerDelay);

        // Handle blur as filter string
        if ('filter' in initial) {
            const blurFrom = enter.blur?.[0] ?? 0;
            (initial as Record<string, unknown>).filter = `blur(${blurFrom}px)`;
        }
        if ('filter' in animate) {
            const blurTo = enter.blur?.[1] ?? 0;
            (animate as Record<string, unknown>).filter = `blur(${blurTo}px)`;
        }

        // Exit animation
        let exit: TargetAndTransition | undefined;
        if (animation.exit) {
            exit = extractToState(animation.exit);
            // Attach exit transition
            const exitTransition = buildTransition(animation.exit, 0);
            (exit as Record<string, unknown>).transition = exitTransition;
        }

        return { initial, animate, exit, transition };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [animKey, childIndex, node?._staggerDelay]);

    return {
        ref,
        initial: result.initial,
        animate: isVisible ? result.animate : result.initial,
        exit: result.exit,
        transition: result.transition,
        isVisible,
    };
}
